/**
 * Indian-format amount in words (spec §4 "Rounding rules", §8.1).
 *
 * This exists to permanently kill the hand-typed defects on the real invoices:
 * "TWEVEL thousand NINE hundred EIGHTY" (#66) and "SEVEN thousand NINE hundred
 * SIXTH FIVE" (#65).
 *
 * Output shape: `Rupees Seven Thousand Nine Hundred Sixty Five Only`
 *   - Indian numbering: crore / lakh / thousand / hundred
 *   - Title Case words, single spaces, no hyphens
 *   - no "and" between hundreds and tens
 *   - sub-rupee amounts append `and Paise <words>` before `Only`
 */
import { assertInteger } from "./rounding.js";
const ONES = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
];
const TENS = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
];
const CRORE = 10_000_000;
const LAKH = 100_000;
const THOUSAND = 1_000;
const HUNDRED = 100;
function lookup(table, index, label) {
    const word = table[index];
    if (word === undefined) {
        throw new Error(`amountInWords: no ${label} word for index ${index}`);
    }
    return word;
}
/** Words for 1..99. */
function belowHundred(value) {
    if (value < 20) {
        return lookup(ONES, value, "unit");
    }
    const tens = Math.floor(value / 10);
    const units = value % 10;
    const tensWord = lookup(TENS, tens, "tens");
    return units === 0 ? tensWord : `${tensWord} ${lookup(ONES, units, "unit")}`;
}
/**
 * Words for a non-negative integer using the Indian numbering system.
 * The crore group recurses, so 1,00,00,00,000 reads "One Hundred Crore".
 */
export function integerToIndianWords(value) {
    assertInteger(value, "integerToIndianWords value");
    if (value < 0) {
        throw new Error(`integerToIndianWords value must not be negative, received ${value}`);
    }
    if (value === 0) {
        return "Zero";
    }
    const parts = [];
    let rest = value;
    const crore = Math.floor(rest / CRORE);
    rest -= crore * CRORE;
    if (crore > 0) {
        parts.push(integerToIndianWords(crore), "Crore");
    }
    const lakh = Math.floor(rest / LAKH);
    rest -= lakh * LAKH;
    if (lakh > 0) {
        parts.push(belowHundred(lakh), "Lakh");
    }
    const thousand = Math.floor(rest / THOUSAND);
    rest -= thousand * THOUSAND;
    if (thousand > 0) {
        parts.push(belowHundred(thousand), "Thousand");
    }
    const hundred = Math.floor(rest / HUNDRED);
    rest -= hundred * HUNDRED;
    if (hundred > 0) {
        parts.push(lookup(ONES, hundred, "unit"), "Hundred");
    }
    if (rest > 0) {
        parts.push(belowHundred(rest));
    }
    return parts.join(" ");
}
/**
 * Renders an integer paise amount as an Indian-format amount in words.
 *
 * @example amountInWords(796500) === "Rupees Seven Thousand Nine Hundred Sixty Five Only"
 * @example amountInWords(0)      === "Rupees Zero Only"
 * @example amountInWords(796550) === "Rupees Seven Thousand Nine Hundred Sixty Five and Paise Fifty Only"
 */
export function amountInWords(paise) {
    assertInteger(paise, "amountInWords paise");
    const isNegative = paise < 0;
    const abs = isNegative ? -paise : paise;
    const rupees = Math.floor(abs / 100);
    const remainingPaise = abs - rupees * 100;
    const parts = ["Rupees"];
    if (isNegative) {
        parts.push("Minus");
    }
    parts.push(integerToIndianWords(rupees));
    if (remainingPaise > 0) {
        parts.push("and", "Paise", integerToIndianWords(remainingPaise));
    }
    parts.push("Only");
    return parts.join(" ");
}
//# sourceMappingURL=amount-in-words.js.map